import {
  AdditionalSuccessfulLinkInfo,
  MergeFileStorageData,
  ValidationErrors,
} from "../config/types";

export const PARENT_ORIGIN_PARAM = "parent_origin";

// The origin messages from this page carry. That's window.origin, which only differs
// from location.origin in about:blank, srcdoc and sandboxed frames, where one of the
// two is "null". A page can also overwrite window.origin with a global `var origin`,
// so where neither is "null", location.origin is the one to trust.
export const getPageOrigin = (windowOrigin: unknown, locationOrigin: string) =>
  typeof windowOrigin === "string" && (windowOrigin === "null" || locationOrigin === "null")
    ? windowOrigin
    : locationOrigin;

// Only the page that embeds Link may drive it. That page is the customer's, so its
// origin can't be allowlisted: initialize.js passes it in the iframe URL instead. A
// customer tab that loaded initialize.js before it did so sends no parent_origin, so
// for that one the source check has to hold on its own.
export const isFromParent = (
  source: unknown,
  origin: string,
  parent: unknown,
  parentOrigin: string | null
) => {
  if (source !== parent) {
    return false;
  }
  if (parentOrigin != null && origin !== parentOrigin) {
    console.warn(
      `Link ignored a message from the page embedding it: sent from ${origin}, but initialize.js ran on ${parentOrigin}`
    );
    return false;
  }
  return true;
};

// The embedding page only takes messages from Link's own iframe, at the origin it
// loaded Link from. Any other frame on the page could otherwise fake a success, a
// close or a file selection. A sandboxed page's frames are sandboxed too, so Link
// embedded in one has an opaque origin and its messages come from "null" instead.
export const isFromLink = (
  source: unknown,
  origin: string,
  link: unknown,
  linkOrigin: string,
  embedderOrigin: string
) => {
  if (source == null || source !== link) {
    return false;
  }
  if (origin !== linkOrigin && !(origin === "null" && embedderOrigin === "null")) {
    console.warn(`Ignored a message from the Link iframe: sent from ${origin}, not ${linkOrigin}`);
    return false;
  }
  return true;
};

export interface ParentMessage {
  linkToken: string;
  forAdmin: boolean;
  apiBaseURL: string;
  messageType: string;
  isLinkOpen: boolean;
  isForMagicLink: boolean;
  initialPageResponseBody: any;
}

/**
 * Calls onMessage with each message from the page that embeds Link. Returns a
 * function that stops listening.
 */
export const listenToParent = (onMessage: (event: MessageEvent<ParentMessage>) => void) => {
  const parentOrigin = new URLSearchParams(window.location.search).get(PARENT_ORIGIN_PARAM);
  const listener = (event: MessageEvent<ParentMessage>) => {
    if (!isFromParent(event.source, event.origin, window.parent, parentOrigin)) {
      return;
    }
    if (!event.data || typeof event.data !== "object") {
      return;
    }
    onMessage(event);
  };
  window.addEventListener("message", listener);
  return () => window.removeEventListener("message", listener);
};

export const passValidationErrors = (
  validationErrors: ValidationErrors,
  authorizationCode?: string
) => {
  window.parent.postMessage(
    {
      messageType: "SEND_VALIDATION_ERRORS_TO_PARENT",
      validationErrors: validationErrors,
      authorization_code: authorizationCode,
    },
    "*"
  );
};

export const passLinkToken = ({
  publicToken,
  shouldSendTokenOnSuccessfulLink,
  additionalInfo,
  authorizationCode,
}: {
  publicToken: string;
  shouldSendTokenOnSuccessfulLink?: boolean;
  additionalInfo?: AdditionalSuccessfulLinkInfo;
  authorizationCode?: string;
}) =>
  window.parent.postMessage(
    {
      messageType: "SEND_PUBLIC_TOKEN_TO_PARENT",
      publicToken,
      shouldSendTokenOnSuccessfulLink,
      additionalInfo,
      authorization_code: authorizationCode,
    },
    "*"
  );

export const passSuccess = (authorizationCode?: string) => {
  window.parent.postMessage(
    {
      messageType: "SEND_SUCCESS",
      authorization_code: authorizationCode,
    },
    "*"
  );
};

export const exitAndOptionallyPassLinkToken = ({
  publicToken,
  setIsWaitingForReopenConfirmation,
  additionalInfo,
  authorizationCode,
}: {
  publicToken: string | undefined;
  setIsWaitingForReopenConfirmation: (isWaitingForReopenConfirmation: boolean) => void;
  additionalInfo?: AdditionalSuccessfulLinkInfo;
  authorizationCode?: string;
}) => {
  if (publicToken) {
    passLinkToken({ publicToken, additionalInfo, authorizationCode });
  } else {
    window.parent.postMessage({
      messageType: "CLOSE_LINKING_FLOW",
      authorization_code: authorizationCode,
    }, "*");
  }
  setIsWaitingForReopenConfirmation(true);
};

export const closeLinkingFlowWithoutPassingLinkToken = (
  setIsWaitingForReopenConfirmation: (isWaitingForReopenConfirmation: boolean) => void
) =>
  exitAndOptionallyPassLinkToken({
    publicToken: undefined,
    setIsWaitingForReopenConfirmation,
  });

export const sendSelectedFiles = (selectedFiles: Array<MergeFileStorageData>) => {
  window.parent.postMessage({ messageType: "SEND_SELECTED_FILES_TO_PARENT", selectedFiles }, "*");
};
